import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { type Feedback } from "@/services/api";
import { getSentimentClasses } from "@/lib/sentiment";

interface FeedbackDetailModalProps {
  feedback: (Feedback & { productName?: string }) | null;
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackDetailModal = ({ feedback, isOpen, onClose }: FeedbackDetailModalProps) => {
  if (!feedback) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Feedback Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Header Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Product</label>
                  <p className="text-sm font-semibold">{feedback.productName || 'Unknown Product'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Submitted</label>
                  <p className="text-sm">{formatDate(feedback.submittedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback Analysis */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Feedback Analysis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Summary</label>
                <p className="text-sm leading-relaxed bg-muted/30 rounded-lg p-3">
                  {feedback.summary || 'No summary available'}
                </p>
              </div>
              
              {/* Sentiment and Tags */}
              <div className="grid grid-cols-2 gap-4">
                {feedback.sentiment && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-2 block">Sentiment</label>
                    <Badge className={`${getSentimentClasses(feedback.sentiment)} border`}>
                      {feedback.sentiment}
                    </Badge>
                  </div>
                )}
                
                {feedback.tags && feedback.tags.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-2 block">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {feedback.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Full Feedback</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/30 rounded-lg p-4 border">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{feedback.content}</p>
              </div>
            </CardContent>
          </Card>

          {/* Technical Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Technical Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Feedback ID</label>
                  <p className="font-mono text-xs bg-muted/50 px-2 py-1 rounded mt-1">{feedback.id}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Product ID</label>
                  <p className="font-mono text-xs bg-muted/50 px-2 py-1 rounded mt-1">{feedback.productId}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDetailModal;
